import { fetchAuthSession } from "aws-amplify/auth";
import {
  DATA_BUCKET_NAME,
  COGNITO_IDENTITYPOOL_ID,
  COGNITO_USERPOOL_ID,
  COGNITO_USERPOOL_WEBCLIENT_ID,
  DRONESQUARE_BACKEND,
  DRONESQUARE_API_GATEWAY,
} from "./domains/shared/constants/constants";
import { makeObjectFromParameters } from "./domains/shared/utils/aws-utils";
import { ResourcesConfig } from "aws-amplify";

// parameter store 에서 받아옴
// 최대 10개까지만 받을 수 있어서 api 로 교체
// const { Parameters } = await ssm.getParameters(params, {});

// parameter store 에서 값 가져오는 api

interface SsmParameter {
  Name: string;
  Value: string;
}

const SSM_FETCH_ATTEMPTS = 3;
const SSM_RETRY_BASE_MS = 300;

// 이름·값이 모두 문자열이 아닌 원소는 조용히 건너뛴다(src/server/ssm/params.ts 의 loadSsmParams 와 동일 규칙) —
// 배열 안에 null·숫자 등이 섞여도 여기서 소거되므로 뒤따르는 맵 변환이 throw 하지 않는다.
async function fetchValidSsmParametersOnce(): Promise<SsmParameter[]> {
  const response = await fetch(process.env.NEXT_PUBLIC_SSM_URL!);
  if (!response.ok) {
    throw new Error(`SSM 파라미터 조회 실패(http_${response.status})`);
  }
  const payload: unknown = await response.json();
  if (!Array.isArray(payload)) {
    throw new Error("SSM 파라미터 조회 실패(응답이 배열이 아님)");
  }
  return payload.filter((element): element is SsmParameter => {
    const candidate = element as Partial<SsmParameter> | null;
    return (
      typeof candidate?.Name === "string" && typeof candidate?.Value === "string"
    );
  });
}

// 서버(SSR)에서 모듈 평가가 throw 하면 그 실패가 웜 인스턴스의 모듈 캐시에 박제되어,
// 일시 네트워크 장애가 지나간 뒤에도 dynamic 라우트 전체가 영구 500 이 된다(2026-08-13 dev 실측).
// 서버는 빈 설정으로 셸 렌더를 계속하고 — 클라이언트가 브라우저에서 자체 fetch 로 복구한다.
// 브라우저는 전 시도 실패 시 throw 로 fail-loud 유지(설정 없인 앱이 동작 불가, 침묵 오동작 방지).
// 조회 자체가 성공했는데 응답이 빈 배열이면 이 함수는 환경과 무관하게 빈 배열을 그대로 돌려준다.
async function fetchSsmParametersOrEmptyOnServer(): Promise<SsmParameter[]> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= SSM_FETCH_ATTEMPTS; attempt += 1) {
    try {
      return await fetchValidSsmParametersOnce();
    } catch (error) {
      lastError = error;
      if (attempt < SSM_FETCH_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, attempt * SSM_RETRY_BASE_MS));
      }
    }
  }
  if (typeof window === "undefined") {
    console.error(
      "[awsconfig] SSM 파라미터 조회 실패 — 서버 렌더는 빈 설정으로 계속한다(클라이언트가 자체 재시도)",
      lastError
    );
    return [];
  }
  throw new Error(`SSM 파라미터 조회 실패: ${String(lastError)}`);
}

const Parameters = await fetchSsmParametersOrEmptyOnServer();

/**
 * SSM 파라미터 맵. **항상 채워져 있다고 가정하면 안 된다.**
 *
 * 빈 객체(`{}`)가 되는 경로는 셋이다 — (1) 서버(SSR) 평가에서 `fetchSsmParametersOrEmptyOnServer` 의
 * 전 시도(응답 실패·비배열 응답 포함)가 실패한 경우, (2) 조회가 성공했지만 응답이 빈 배열인 경우,
 * (3) 조회가 성공했지만 `fetchValidSsmParametersOnce` 의 필터를 통과한 유효 원소가 하나도 없는 경우
 * (예: `[null, 123]`). (2)·(3) 은 환경과 무관하다. 전 시도 실패를 throw 로 바꾸는 것은 브라우저 평가뿐이며,
 * 그때만 모듈 자체가 로드되지 않는다.
 *
 * 배열 원소는 `Name`·`Value` 가 모두 문자열인 것만 `fetchValidSsmParametersOnce` 의 필터를 통과한다 —
 * `null`·숫자 같은 비정형 원소가 섞여도 예외 없이 그 원소만 소거되고, 그 결과 해당 키 조회가
 * `undefined` 가 된다.
 *
 * 따라서 소비자는 서버·브라우저 어느 쪽이든 `configObject[KEY]` 의 `undefined` 를 처리해야 한다.
 */
const ssmConfig = makeObjectFromParameters(Parameters);

/**
 * 로컬 백엔드 오버라이드 — `NEXT_PUBLIC_LOCAL_BACKEND` 가 있을 때만 선다.
 *
 * SSM 의 `dronesquare_backend` 는 환경당 하나뿐이라(dev 는 `https://dev.main-api.dronesquare.kr/v1/`),
 * 아직 배포되지 않은 백엔드 브랜치를 화면으로 확인할 길이 없었다. 값이 없으면 분기 자체가 서지
 * 않으므로 배포 빌드의 동작은 불변이다.
 *
 * **`.gitignore` 는 이 값을 막지 못한다.** Next 는 `process.env.NEXT_PUBLIC_*` 를 빌드 시점에
 * 번들로 **인라인**하고, 그 출처는 git 이 아니라 빌드 기계의 `.env*.local` 이다. 자매 레포에서
 * 실측했다(2026-09-02) — `.env` 에 값을 둔 채 빌드하면 산출 번들에 `127.0.0.1:8000` 이 박힌다.
 * 배포가 안전한 이유는 무시 규칙이 아니라 **CI 가 clean checkout 에서 빌드해 그 기계에 그 파일
 * 자체가 없다**는 것이다. 그러므로 쓸 때만 켜고 끝나면 다시 주석 처리한다.
 *
 * 덮는 키를 `dronesquare_backend` 하나로 한정한다 — 버킷·Cognito 풀까지 로컬로 바꾸면 로컬 서버가
 * 보는 자원과 화면이 보는 자원이 갈려 "로컬에서 됐는데" 를 만든다.
 */
export const configObject: Record<string, string> = process.env.NEXT_PUBLIC_LOCAL_BACKEND
  ? { ...ssmConfig, [DRONESQUARE_BACKEND]: process.env.NEXT_PUBLIC_LOCAL_BACKEND }
  : ssmConfig;

/**
 * Creates a custom header function for API endpoints that includes the auth token
 * @returns Authorization header with JWT token from current session
 */
const createAuthHeader = async () => {
  const session = await fetchAuthSession();
  return {
    Authorization: session.tokens?.idToken?.toString() ?? "",
  };
};

/**
 * Creates an API endpoint configuration
 * @param name - API endpoint name
 * @param endpoint - API endpoint URL (optional)
 * @param requiresAuth - Whether the endpoint requires authentication (default: true)
 * @returns API endpoint configuration
 */
function createApiEndpoint(
  name: string,
  endpoint?: string,
  requiresAuth: boolean = true
) {
  return {
    name,
    ...(endpoint && { endpoint }),
    ...(requiresAuth && { custom_header: createAuthHeader }),
  };
}

// Amplify v6 Gen1 설정 (v5 스타일 호환, v6에서도 지원)
/**
 * `Storage.S3.region`(빌드타임 `NEXT_PUBLIC_REGION` env 유래)을 제외한 값이 {@link configObject} 에서
 * 파생된다 — 타입은 `string` 이지만 `configObject` 가 비었거나
 * ({@link configObject} 에 열거된 세 경로) 일부 키가 빠지면 `userPoolId`·`bucket`·`endpoint` 등이
 * 런타임에 `undefined` 다.
 * 즉 이 객체의 존재는 "Amplify 를 설정할 값이 갖춰졌다"를 보장하지 않는다.
 */
export const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: configObject[COGNITO_USERPOOL_ID],
      userPoolClientId: configObject[COGNITO_USERPOOL_WEBCLIENT_ID],
      identityPoolId: configObject[COGNITO_IDENTITYPOOL_ID],
    },
  },
  Storage: {
    S3: {
      bucket: configObject[DATA_BUCKET_NAME],
      region: process.env.NEXT_PUBLIC_REGION!,
    },
  },
  API: {
    REST: {
      [DRONESQUARE_API_GATEWAY]: {
        endpoint: configObject[DRONESQUARE_BACKEND],
      },
    },
  },
};
